import { z } from 'zod';

/**
 * Client-side mirrors of the DataAnnotations on `Dtos/Auth/AuthContracts.cs`.
 *
 * Deliberately no stricter than the server: these exist to spare the user a
 * round trip, not to enforce anything. The real password policy is Identity's
 * `PasswordOptions`, which lives on the server and reports through
 * `ValidationProblemDetails` — see `useServerValidation`. Adding rules here that
 * the server does not have would reject passwords the server would accept.
 *
 * Built through functions rather than declared as constants so the messages can
 * be translated; `t` is called at render time, and the schema is memoised
 * against the active language by each screen.
 */
type Translate = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

/**
 * Mirrors `Identity:Password:RequiredLength` (appsettings.json), which is what the
 * server enforces, as does `MinimumLength` on the DTOs in `Dtos/Auth/AuthContracts.cs`.
 * Change all of them together, or the hint and this check promise a length the server rejects.
 */
export const PASSWORD_MIN_LENGTH = 12;

/** `[Required]` + `[StringLength(100)]`, with the minimum taken from Identity's policy. */
function newPasswordField(t: Translate) {
  return z
    .string()
    .min(1, t('auth:Auth.Validation.NewPasswordRequired', 'New password is required'))
    .min(
      PASSWORD_MIN_LENGTH,
      t('auth:Auth.Validation.PasswordLength', 'Password must be at least {{minLength}} characters long', {
        minLength: PASSWORD_MIN_LENGTH,
      }),
    )
    .max(100, t('auth:Auth.Validation.PasswordTooLong', 'Password must be at most 100 characters long'));
}

/** Matches `[Required]` + `[Compare(nameof(NewPassword))]`. */
function confirmPasswordField(t: Translate) {
  return z.string().min(1, t('auth:Auth.Validation.ConfirmRequired', 'Please confirm your new password'));
}

/** The `[Compare]` check, reported on the confirmation input as Razor did. */
function matchesConfirmation(t: Translate) {
  return {
    check: (value: { newPassword: string; confirmPassword: string }) => value.newPassword === value.confirmPassword,
    options: {
      message: t('auth:Auth.Error.PasswordMismatch', 'New password and confirmation do not match'),
      path: ['confirmPassword' as const],
    },
  };
}

export function loginSchema(t: Translate) {
  return z.object({
    email: z
      .string()
      .min(1, t('auth:Auth.Validation.EmailRequired', 'Email is required'))
      .email(t('auth:Auth.Validation.EmailInvalid', 'Enter a valid email address')),
    password: z.string().min(1, t('auth:Auth.Validation.PasswordRequired', 'Password is required')),
  });
}

export function forgotPasswordSchema(t: Translate) {
  return z.object({
    email: z
      .string()
      .min(1, t('auth:Auth.Validation.EmailRequired', 'Email is required'))
      .email(t('auth:Auth.Validation.EmailInvalid', 'Enter a valid email address')),
  });
}

/**
 * Shared by reset and activation: both forms collect only the new password, and
 * both carry the user id and token in the query string rather than the form.
 */
export function newPasswordSchema(t: Translate) {
  const confirmation = matchesConfirmation(t);

  return z
    .object({
      newPassword: newPasswordField(t),
      confirmPassword: confirmPasswordField(t),
    })
    .refine(confirmation.check, confirmation.options);
}

export function changePasswordSchema(t: Translate) {
  const confirmation = matchesConfirmation(t);

  return z
    .object({
      currentPassword: z
        .string()
        .min(1, t('auth:Auth.Validation.CurrentPasswordRequired', 'Current password is required')),
      newPassword: newPasswordField(t),
      confirmPassword: confirmPasswordField(t),
    })
    .refine(confirmation.check, confirmation.options);
}

/**
 * A six-digit second-factor code. The input strips spaces before it gets here,
 * since authenticator apps display codes as "123 456".
 */
export function mfaCodeSchema(t: Translate) {
  return z.object({
    code: z
      .string()
      .min(1, t('auth:Auth.Validation.CodeRequired', 'Verification code is required'))
      .regex(/^\d{6}$/, t('auth:Auth.Validation.CodeFormat', 'Enter the 6-digit code')),
  });
}

/**
 * A single-use recovery code.
 *
 * Identity issues these as `XXXXX-XXXXX` from an unambiguous alphabet — digits
 * 2-9 and consonants, with no 0/1/O/I/S/Z to be misread. The check here is
 * deliberately looser than that alphabet: the server normalises case, spacing
 * and the separator before redeeming, so the only thing worth catching in the
 * browser is a length that could not possibly be a code. Anything else is the
 * server's call.
 */
export function recoveryCodeSchema(t: Translate) {
  return z.object({
    recoveryCode: z
      .string()
      .min(1, t('auth:Auth.Validation.RecoveryCodeRequired', 'Recovery code is required'))
      .refine(
        (value) => value.replace(/[^a-zA-Z0-9]/g, '').length === 10,
        t('auth:Auth.Validation.RecoveryCodeFormat', 'Enter a recovery code in the form XXXXX-XXXXX'),
      ),
  });
}

/** Turning MFA off requires the current password — see `MfaService.DisableAsync`. */
export function disableMfaSchema(t: Translate) {
  return z.object({
    currentPassword: z
      .string()
      .min(1, t('auth:Auth.Validation.CurrentPasswordRequired', 'Current password is required')),
  });
}

export type LoginValues = z.infer<ReturnType<typeof loginSchema>>;
export type MfaCodeValues = z.infer<ReturnType<typeof mfaCodeSchema>>;
export type RecoveryCodeValues = z.infer<ReturnType<typeof recoveryCodeSchema>>;
export type DisableMfaValues = z.infer<ReturnType<typeof disableMfaSchema>>;
export type ForgotPasswordValues = z.infer<ReturnType<typeof forgotPasswordSchema>>;
export type NewPasswordValues = z.infer<ReturnType<typeof newPasswordSchema>>;
export type ChangePasswordValues = z.infer<ReturnType<typeof changePasswordSchema>>;
